import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Trash2,
  Edit2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Save,
  Eye,
  EyeOff,
  Power,
  Wifi,
  WifiOff,
  Calendar,
  Key,
  Globe,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { api } from "@/services/api";
import { toast } from "sonner";

// ─── Section wrapper ────────────────────────────────────────────────────────
const Section = ({ icon: Icon, title, subtitle, delay = 0, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="space-y-6"
  >
    <div className="pb-4 border-b border-slate-200">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-indigo-50">
          <Icon className="h-5 w-5 text-indigo-700" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            {title}
          </h2>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
    {children}
  </motion.div>
);

// ─── Main Component ──────────────────────────────────────────────────────────
export const SettingsPage = () => {
  const [pageLoading, setPageLoading] = useState(true);

  // API Config
  const [apiForm, setApiForm] = useState({
    graphql_endpoint: "",
    client_id: "",
    client_secret: "",
    tenant_id: "",
  });
  const [apiMeta, setApiMeta] = useState({ client_id_set: false, client_secret_set: false, tenant_id_set: false });
  const [showSecret, setShowSecret] = useState(false);
  const [showClientId, setShowClientId] = useState(false);
  const [showTenantId, setShowTenantId] = useState(false);
  const [savingApi, setSavingApi] = useState(false);
  const [testingConn, setTestingConn] = useState(false);
  const [connStatus, setConnStatus] = useState(null); // null | 'ok' | 'error'

  // Active Event
  const [eventForm, setEventForm] = useState({ event_id: "", event_name: "", event_year: "" });
  const [savingEvent, setSavingEvent] = useState(false);

  // Registration Types
  const [regTypes, setRegTypes] = useState([]);
  const [regTypesLoading, setRegTypesLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [regForm, setRegForm] = useState({
    name: "",
    description: "",
    eventsair_type_id: "",
    is_approved_type: false,
    is_declined_type: false,
  });

  // ── Load all settings on mount ──────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    try {
      const [apiConf, eventConf, types] = await Promise.all([
        api.settings.getApiConfig(),
        api.settings.getActiveEvent(),
        api.registrationTypes.list(),
      ]);

      setApiMeta({
        client_id_set: apiConf.client_id_set,
        client_secret_set: apiConf.client_secret_set,
        tenant_id_set: apiConf.tenant_id_set,
      });
      setApiForm({
        graphql_endpoint: apiConf.graphql_endpoint || "",
        client_id: "",
        client_secret: "",
        tenant_id: "",
      });
      setEventForm({
        event_id: eventConf.event_id || "",
        event_name: eventConf.event_name || "",
        event_year: eventConf.event_year || "",
      });
      setRegTypes(types);
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setPageLoading(false);
      setRegTypesLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // ── API Config handlers ─────────────────────────────────────────────────
  const handleSaveApi = async () => {
    setSavingApi(true);
    try {
      const payload = { graphql_endpoint: apiForm.graphql_endpoint };
      if (apiForm.client_id) payload.client_id = apiForm.client_id;
      if (apiForm.client_secret) payload.client_secret = apiForm.client_secret;
      if (apiForm.tenant_id) payload.tenant_id = apiForm.tenant_id;
      await api.settings.updateApiConfig(payload);
      toast.success("API configuration saved");
      setConnStatus(null);
      // Clear plain-text fields after save
      setApiForm(f => ({ ...f, client_id: "", client_secret: "", tenant_id: "" }));
      const fresh = await api.settings.getApiConfig();
      setApiMeta({ client_id_set: fresh.client_id_set, client_secret_set: fresh.client_secret_set, tenant_id_set: fresh.tenant_id_set });
    } catch (e) {
      toast.error(e.message || "Failed to save API configuration");
    } finally {
      setSavingApi(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConn(true);
    setConnStatus(null);
    try {
      const result = await api.settings.testConnection();
      if (result.success) {
        setConnStatus("ok");
        toast.success(result.message);
      } else {
        setConnStatus("error");
        toast.error(result.message);
      }
    } catch (e) {
      setConnStatus("error");
      toast.error(e.message || "Connection test failed");
    } finally {
      setTestingConn(false);
    }
  };

  // ── Active Event handlers ───────────────────────────────────────────────
  const handleSaveEvent = async () => {
    setSavingEvent(true);
    try {
      await api.settings.updateApiConfig(eventForm);
      toast.success("Active event saved");
    } catch (e) {
      toast.error(e.message || "Failed to save event configuration");
    } finally {
      setSavingEvent(false);
    }
  };

  // ── Registration Type handlers ──────────────────────────────────────────
  const resetRegForm = () => {
    setRegForm({ name: "", description: "", eventsair_type_id: "", is_approved_type: false, is_declined_type: false });
    setEditingType(null);
  };

  const openEditDialog = (type) => {
    setEditingType(type);
    setRegForm({
      name: type.name,
      description: type.description || "",
      eventsair_type_id: type.eventsair_type_id || "",
      is_approved_type: type.is_approved_type,
      is_declined_type: type.is_declined_type,
    });
    setDialogOpen(true);
  };

  const handleRegSubmit = async (e) => {
    e.preventDefault();
    if (!regForm.name) { toast.error("Name is required"); return; }
    if (regForm.is_approved_type && regForm.is_declined_type) {
      toast.error("A type cannot be both Approved and Declined");
      return;
    }
    try {
      if (editingType) {
        await api.registrationTypes.update(editingType.id, regForm);
        toast.success("Registration type updated");
      } else {
        await api.registrationTypes.create(regForm);
        toast.success("Registration type created");
      }
      setDialogOpen(false);
      resetRegForm();
      setRegTypes(await api.registrationTypes.list());
    } catch (e) {
      toast.error(e.message || "Failed to save registration type");
    }
  };

  const handleDeleteReg = async (id) => {
    if (!window.confirm("Delete this registration type?")) return;
    try {
      await api.registrationTypes.delete(id);
      toast.success("Deleted");
      setRegTypes(await api.registrationTypes.list());
    } catch (e) {
      toast.error(e.message || "Failed to delete");
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-12 max-w-4xl" data-testid="settings-page">

        {/* Page header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Settings
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Manage EventsAir API credentials, the active event, and registration types.
          </p>
        </div>

        {/* ── Section 1: API Configuration ── */}
        <Section icon={Key} title="API Configuration" subtitle="EventsAir credentials — stored encrypted in the database." delay={0}>
          <div className="grid grid-cols-1 gap-5">
            {/* Base URL */}
            <div className="space-y-1.5">
              <Label htmlFor="graphql_endpoint">Base URL</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="graphql_endpoint"
                  className="pl-9"
                  value={apiForm.graphql_endpoint}
                  onChange={e => setApiForm(f => ({ ...f, graphql_endpoint: e.target.value }))}
                  placeholder="https://api.eventsair.com/graphql"
                />
              </div>
            </div>

            {/* Client ID */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="client_id">Client ID (API Key)</Label>
                {apiMeta.client_id_set && (
                  <span className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Saved
                  </span>
                )}
              </div>
              <div className="relative">
                <Input
                  id="client_id"
                  type={showClientId ? "text" : "password"}
                  value={apiForm.client_id}
                  onChange={e => setApiForm(f => ({ ...f, client_id: e.target.value }))}
                  placeholder={apiMeta.client_id_set ? "Leave blank to keep existing value" : "Enter Client ID"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute inset-y-0 right-0 h-full px-3"
                  onClick={() => setShowClientId(v => !v)}
                >
                  {showClientId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Client Secret */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="client_secret">Client Secret (API Secret)</Label>
                {apiMeta.client_secret_set && (
                  <span className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Saved
                  </span>
                )}
              </div>
              <div className="relative">
                <Input
                  id="client_secret"
                  type={showSecret ? "text" : "password"}
                  value={apiForm.client_secret}
                  onChange={e => setApiForm(f => ({ ...f, client_secret: e.target.value }))}
                  placeholder={apiMeta.client_secret_set ? "Leave blank to keep existing value" : "Enter Client Secret"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute inset-y-0 right-0 h-full px-3"
                  onClick={() => setShowSecret(v => !v)}
                >
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Tenant ID */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="tenant_id">Tenant ID (Azure AD)</Label>
                {apiMeta.tenant_id_set && (
                  <span className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Saved
                  </span>
                )}
              </div>
              <div className="relative">
                <Input
                  id="tenant_id"
                  type={showTenantId ? "text" : "password"}
                  value={apiForm.tenant_id}
                  onChange={e => setApiForm(f => ({ ...f, tenant_id: e.target.value }))}
                  placeholder={apiMeta.tenant_id_set ? "Leave blank to keep existing value" : "Enter Tenant ID"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute inset-y-0 right-0 h-full px-3"
                  onClick={() => setShowTenantId(v => !v)}
                >
                  {showTenantId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Connection status indicator */}
          {connStatus && (
            <div className={`flex items-center gap-2 px-4 py-3 rounded-md text-sm ${connStatus === "ok" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
              {connStatus === "ok"
                ? <><Wifi className="h-4 w-4" /> Connection successful — credentials are valid.</>
                : <><WifiOff className="h-4 w-4" /> Connection failed — check your credentials.</>
              }
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testingConn}
            >
              {testingConn
                ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Testing…</>
                : <><Power className="h-4 w-4 mr-2" /> Test Connection</>
              }
            </Button>
            <Button
              className="bg-indigo-950 hover:bg-indigo-900"
              onClick={handleSaveApi}
              disabled={savingApi}
            >
              {savingApi
                ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                : <><Save className="h-4 w-4 mr-2" /> Save API Settings</>
              }
            </Button>
          </div>
        </Section>

        {/* ── Section 2: Active Event ── */}
        <Section icon={Calendar} title="Active Event" subtitle="Only one event is active at a time. All registrants and approvals are tied to this event." delay={0.05}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="space-y-1.5 sm:col-span-3">
              <Label htmlFor="event_id">Event ID</Label>
              <Input
                id="event_id"
                value={eventForm.event_id}
                onChange={e => setEventForm(f => ({ ...f, event_id: e.target.value }))}
                placeholder="e.g., ed0edbc4-61b7-4205-a22c-8d8eb22c9435"
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">The EventsAir event GUID used for all API calls.</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="event_name">Event Name</Label>
              <Input
                id="event_name"
                value={eventForm.event_name}
                onChange={e => setEventForm(f => ({ ...f, event_name: e.target.value }))}
                placeholder="e.g., Annual Meetings 2026"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="event_year">Year</Label>
              <Input
                id="event_year"
                value={eventForm.event_year}
                onChange={e => setEventForm(f => ({ ...f, event_year: e.target.value }))}
                placeholder="e.g., 2026"
              />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              className="bg-indigo-950 hover:bg-indigo-900"
              onClick={handleSaveEvent}
              disabled={savingEvent}
            >
              {savingEvent
                ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                : <><Save className="h-4 w-4 mr-2" /> Save Event Settings</>
              }
            </Button>
          </div>
        </Section>

        {/* ── Section 3: Registration Types ── */}
        <Section icon={CheckCircle2} title="Registration Types" subtitle="Map internal types to EventsAir type IDs for approve/decline workflows." delay={0.1}>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => { resetRegForm(); setDialogOpen(true); }}
              data-testid="add-type-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Type
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Name</TableHead>
                  <TableHead>EventsAir Type ID</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {regTypesLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto text-slate-300" />
                    </TableCell>
                  </TableRow>
                ) : regTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10 text-slate-400 text-sm">
                      No registration types configured yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  regTypes.map(type => (
                    <TableRow key={type.id}>
                      <TableCell className="font-medium">{type.name}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">
                        {type.eventsair_type_id || <span className="italic text-slate-300">—</span>}
                      </TableCell>
                      <TableCell>
                        {type.is_approved_type && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Approved
                          </Badge>
                        )}
                        {type.is_declined_type && (
                          <Badge className="bg-red-100 text-red-700 border-red-200">
                            <XCircle className="h-3 w-3 mr-1" /> Declined
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(type)}>
                          <Edit2 className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteReg(type.id)}>
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Section>

        {/* ── Registration Type Dialog ── */}
        <Dialog open={dialogOpen} onOpenChange={open => { setDialogOpen(open); if (!open) resetRegForm(); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>
                {editingType ? "Edit Registration Type" : "Add Registration Type"}
              </DialogTitle>
              <DialogDescription>
                {editingType ? "Update the registration type details." : "Create a new registration type for your event."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleRegSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="typeName">Name *</Label>
                <Input
                  id="typeName"
                  value={regForm.name}
                  onChange={e => setRegForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Standard Attendee"
                  data-testid="type-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="typeDesc">Description</Label>
                <Input
                  id="typeDesc"
                  value={regForm.description}
                  onChange={e => setRegForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional description"
                  data-testid="type-description-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="eventsairId">EventsAir Type ID</Label>
                <Input
                  id="eventsairId"
                  value={regForm.eventsair_type_id}
                  onChange={e => setRegForm(f => ({ ...f, eventsair_type_id: e.target.value }))}
                  placeholder="EventsAir registration type UUID"
                  className="font-mono text-sm"
                  data-testid="type-eventsair-id-input"
                />
                <p className="text-xs text-slate-500">Link this type to an EventsAir registration type for API sync.</p>
              </div>
              <div className="space-y-3 pt-1">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="isApproved"
                    checked={regForm.is_approved_type}
                    onCheckedChange={checked =>
                      setRegForm(f => ({ ...f, is_approved_type: checked, is_declined_type: checked ? false : f.is_declined_type }))
                    }
                    data-testid="type-is-approved-checkbox"
                  />
                  <div>
                    <Label htmlFor="isApproved" className="font-medium cursor-pointer">Set as Approved Type</Label>
                    <p className="text-xs text-slate-500">Registrants will be moved to this type when approved.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="isDeclined"
                    checked={regForm.is_declined_type}
                    onCheckedChange={checked =>
                      setRegForm(f => ({ ...f, is_declined_type: checked, is_approved_type: checked ? false : f.is_approved_type }))
                    }
                    data-testid="type-is-declined-checkbox"
                  />
                  <div>
                    <Label htmlFor="isDeclined" className="font-medium cursor-pointer">Set as Declined Type</Label>
                    <p className="text-xs text-slate-500">Registrants will be moved to this type when declined.</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetRegForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-indigo-950 hover:bg-indigo-900" data-testid="save-type-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {editingType ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      </div>
    </Layout>
  );
};
